import { View, useWindowDimensions } from "react-native";

import { Text } from "react-native-paper";

import { useAppTheme } from "@hooks/useAppTheme";

import { useScenarioBundle } from "../data/scenarios";
import {
	resolveScenarioId,
	useIsActiveScenario,
} from "../hooks/useActiveScenario";
import { useProethicsFlowStore } from "../hooks/useProethicsFlowStore";
import { ResultsScreen } from "./ResultsScreen";

// Matches ProethicsSimulationItem: size the card from the window since the
// session wraps non-stem items without a full-height container.
const SESSION_CHROME = 200;

/**
 * Reflect/report step shown after the simulation. Reads the streamed summative
 * from the shared flow store (submitted when the learner taps Next) and renders
 * it under a directions header. ResultsScreen handles the loading/partial state.
 *
 * Syncing this scenario restores its persisted run, so the report shown always
 * belongs to this step's scenario. Until its conversation is ended (which fires
 * the summative submit), a hint points the learner back to the chat.
 */
export const ProethicsReflectItem = ({
	scenarioId,
	title,
}: {
	scenarioId?: string;
	title?: string;
}) => {
	const { height } = useWindowDimensions();
	// Switching is centralised in the session view; here we only read whether
	// this scenario's run (and report) is the active one.
	const isActive = useIsActiveScenario(scenarioId);
	const summative = useProethicsFlowStore((s) => s.summative);
	const hasSubmitted = useProethicsFlowStore((s) => s.hasSubmitted);
	const theme = useAppTheme();

	const resolved = resolveScenarioId(scenarioId);
	const showReport = isActive && hasSubmitted;
	const personaName = useScenarioBundle(resolved).scenario.persona.name;

	return (
		<View
			style={{
				height: Math.max(520, height - SESSION_CHROME),
				borderWidth: 1,
				borderColor: theme.colors.outlineVariant,
				borderRadius: theme.roundness,
				overflow: "hidden",
				backgroundColor: theme.colors.surface,
			}}
		>
			<View
				style={{
					paddingHorizontal: 16,
					paddingVertical: 12,
					borderBottomWidth: 1,
					borderBottomColor: theme.colors.outlineVariant,
					backgroundColor: theme.colors.surface,
				}}
			>
				<Text
					variant="titleLarge"
					style={{ color: theme.colors.onSurface, marginBottom: 4 }}
				>
					{title || "Reflect"}
				</Text>
				<Text
					variant="bodyMedium"
					style={{ color: theme.colors.onSurfaceVariant }}
				>
					<Text
						variant="bodyMedium"
						style={{
							color: theme.colors.onSurfaceVariant,
							...theme.typography.fonts.bold,
						}}
					>
						Directions:
					</Text>{" "}
					{`Review the feedback from your conversation with ${personaName} to see what you did well and what can be improved. Select the expandable text buttons to access details related to each objective.`}
				</Text>
			</View>
			{showReport ? (
				<ResultsScreen result={summative} scenarioId={resolved} />
			) : (
				<View
					style={{
						flex: 1,
						alignItems: "center",
						justifyContent: "center",
						padding: 24,
					}}
				>
					<Text
						variant="bodyMedium"
						style={{
							color: theme.colors.onSurfaceVariant,
							textAlign: "center",
						}}
					>
						{`Complete the conversation with ${personaName} to see your feedback here.`}
					</Text>
				</View>
			)}
		</View>
	);
};
